/**
 * Token storage in localStorage + hook React per lo stato auth.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiAcceptMembershipInvite,
  apiLogin,
  apiMe,
  apiRegister,
  apiSwitchCompany,
  apiSwitchLocation,
} from './api';
import type {
  AcceptMembershipInviteInput,
  AuthSuccessDto,
  LoginInput,
  MeDto,
  RegisterInput,
  SwitchCompanyInput,
  SwitchLocationInput,
  TalentMeDto,
  TalentSessionDto,
} from './types';

const TOKEN_KEY = 'sg.access_token';

export const authStorage = {
  getToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  setToken(t: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, t);
    } catch {
      /* private mode etc. */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* noop */
    }
  },
};

const ME_KEY = ['auth', 'me'] as const;

export function useAuth() {
  const token = authStorage.getToken();
  const q = useQuery({
    queryKey: ME_KEY,
    queryFn: apiMe,
    enabled: !!token,
    retry: false,
    staleTime: 60_000,
    // All'apertura dell'app rileggi SEMPRE /auth/me: garantisce la lista
    // completa dei locali accessibili (lo switcher) anche se in cache era
    // rimasto lo stub da 1 locale messo al login.
    refetchOnMount: 'always',
  });
  const data = q.data ?? null;
  // Principal "talent-only" (ex-dipendente senza membership): nessun dato
  // operativo, solo la gestione del proprio profilo bacheca. Lo distinguiamo
  // dal membro normale via `principal_type`.
  const isTalent = !!data && (data as TalentMeDto).principal_type === 'talent';
  return {
    me: isTalent ? null : ((data as MeDto | null) ?? null),
    talent: isTalent ? (data as TalentMeDto) : null,
    isLoading: q.isLoading && !!token,
    isLoggedIn: !!data,
    error: q.error,
  };
}

/**
 * Applica un AuthSuccess al client: salva il token e aggiorna la cache di
 * /auth/me senza un fetch aggiuntivo. Quando l'AuthSuccess non contiene
 * `accessible_locations` (che viene solo da /auth/me), invalido il query
 * per ricaricarle alla prossima necessità.
 */
function applyAuthSuccess(
  qc: ReturnType<typeof useQueryClient>,
  res: AuthSuccessDto | TalentSessionDto,
) {
  authStorage.setToken(res.access_token);
  // Sessione talent-only: nessun company/location/role. Popoliamo la cache
  // di /auth/me con lo snapshot ridotto e basta — niente invalidazioni di
  // dati operativi (questo principal non ne ha alcuno).
  if (res.principal_type === 'talent') {
    qc.setQueryData(ME_KEY, { principal_type: 'talent', user: res.user });
    void qc.refetchQueries({ queryKey: ME_KEY, type: 'all' });
    return;
  }
  // Stub provvisorio del `me` per non sfarlo lampeggiare a vuoto. NB:
  // `accessible_locations` qui è solo un segnaposto (il locale corrente):
  // la lista vera la sa solo /auth/me. Per questo SUBITO sotto forziamo un
  // refetch ATTIVO (non solo invalidate, che agisce solo se c'è già un
  // observer): così lo switcher dei locali non resta mai "congelato" a 1.
  const prev = qc.getQueryData(ME_KEY) as { accessible_locations?: unknown } | undefined;
  qc.setQueryData(ME_KEY, {
    user: res.user,
    current_company: res.current_company,
    current_location: res.current_location,
    role: res.role,
    location_scope: res.location_scope,
    accessible_locations: prev?.accessible_locations ?? [res.current_location],
  });
  // `type: 'all'` è ESSENZIALE: al login non c'è ancora un observer del
  // query `me`, quindi un refetch "active-only" non farebbe nulla e lo
  // switcher resterebbe congelato sullo stub da 1 locale. Con 'all' il
  // refetch parte comunque e la lista vera dei locali entra in cache.
  void qc.refetchQueries({ queryKey: ME_KEY, type: 'all' });
  // Le query di dominio dipendono dalla location corrente — invalidiamo
  // per evitare di mostrare dati di un altro locale.
  qc.invalidateQueries({ queryKey: ['suppliers'] });
  qc.invalidateQueries({ queryKey: ['products'] });
  qc.invalidateQueries({ queryKey: ['batches'] });
  qc.invalidateQueries({ queryKey: ['movements'] });
  qc.invalidateQueries({ queryKey: ['cash'] });
  qc.invalidateQueries({ queryKey: ['employees'] });
  qc.invalidateQueries({ queryKey: ['shifts'] });
  qc.invalidateQueries({ queryKey: ['payroll'] });
  qc.invalidateQueries({ queryKey: ['advances'] });
  qc.invalidateQueries({ queryKey: ['invoices'] });
  qc.invalidateQueries({ queryKey: ['contracts'] });
  qc.invalidateQueries({ queryKey: ['haccp'] });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    // Errore gestito dalla pagina (messaggio dedicato + flusso scegli-azienda
    // che è un 409 ma non è un errore): niente toast globale.
    meta: { silentError: true },
    mutationFn: (input: LoginInput) => apiLogin(input),
    onSuccess: (res) => applyAuthSuccess(qc, res),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: (input: RegisterInput) => apiRegister(input),
    onSuccess: (res) => applyAuthSuccess(qc, res),
  });
}

export function useAcceptMembershipInvite() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: (input: AcceptMembershipInviteInput) =>
      apiAcceptMembershipInvite(input),
    onSuccess: (res) => applyAuthSuccess(qc, res),
  });
}

export function useSwitchCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SwitchCompanyInput) => apiSwitchCompany(input),
    onSuccess: (res) => applyAuthSuccess(qc, res),
  });
}

export function useSwitchLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SwitchLocationInput) => apiSwitchLocation(input),
    onSuccess: (res) => applyAuthSuccess(qc, res),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return () => {
    authStorage.clear();
    qc.removeQueries({ queryKey: ME_KEY });
    qc.clear();
  };
}
